import { useState, useEffect, useCallback, useRef } from "react";
import "../../../styles/pages/ouber.scss";
import { Tag, Space, Typography, Button, Input, Select, Row, Col, DatePicker } from "antd";
import { CalendarOutlined, EyeOutlined, PlusCircleOutlined, SearchOutlined, SyncOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import PvTable from "../../../components/Table/PvTable";
import DataView from "../../../components/DataView/DataView";
import PvCardList from "../../../components/DataView/PvCardList";
import UserViewTripReqModal from "../../../components/Ouber/modals/user/UserViewTripReqModal";
import UserCancelConfirmModal from "../../../components/Ouber/modals/user/UserCancelConfirmModal";
import UserRequestTripModal from "../../../components/Ouber/modals/user/UserRequestTripModal";
import { useOuberService } from "../../../services";
import { showErrorToast, showSuccessToast } from "../../../components/Ouber/ToastNotification";
import OuberPageInfoIcon from "../../../components/Ouber/OuberPageInfoIcon";
import { formatStatusWithActor } from "../../../utils/ouberUtils";

/**
 * Lets an employee view, request, and cancel their own OUber trip requests.
 * Swapped in place of the Trips Calendar via the "My Trips" button.
 */
const UserManageTripReqPage = ({ onBackToCalendar }) => {
  const { dataView } = useSelector((state) => state.generalSlice);
  const { details: profile } = useSelector((state) => state.accountDetailSlice);
  const currentUserId = profile?.id;

  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isCancelConfirmModalVisible, setIsCancelConfirmModalVisible] = useState(false);
  const [isRequestModalVisible, setIsRequestModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [filterDest, setFilterDest] = useState("");
  const [filterPickup, setFilterPickup] = useState("");
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterDate, setFilterDate] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);



  const { service: ouberService } = useOuberService();
  const [data, setData] = useState([]);

  // Fetches the list of trip requests from the service based on active filters
  const fetchRequests = useCallback(async () => {
    try {
      const params = { 
        dropoff_location: filterDest || undefined,
        pickup_venue: filterPickup || undefined,
        status: filterStatus || undefined,
        trip_date: filterDate ? filterDate.format("YYYY-MM-DD") : undefined,
      };

      const response = await ouberService.getTripRequests(params);
      const requestsListRaw = Array.isArray(response) ? response : (response?.data || []);
      const requestsList = requestsListRaw.filter(r => r !== null && r !== undefined);

      const reqs = requestsList.map(r => ({ 
        ...r, 
        key: (r.trip_request_id || Math.random()).toString(), 
        id: (r.trip_request_id || "").toString(), 
        dest: r.dropoff_location, 
        pickup: r.pickup_venue, 
        date: r.trip_date ? r.trip_date.split("T")[0] : null, 
        trip_date: r.trip_date ? r.trip_date.split("T")[0] : null, 
        remarks: r.remarks, 
        status: r.request_status,
        name: r.external_passenger_name || (r.employee ? `${r.employee.first_name} ${r.employee.last_name}` : "N/A"),
        unit: r.unit || "N/A"
      }));
      
      // Formats the raw backend data into a consistent UI-friendly object structure
      setData(reqs);
    } catch (err) {
      showErrorToast("Fetch Error", "Failed to load trip requests.");
    }
  }, [filterDest, filterPickup, filterStatus, filterDate, ouberService]);

  const fetchRequestsRef = useRef(fetchRequests);
  useEffect(() => {
    fetchRequestsRef.current = fetchRequests;
  }, [fetchRequests]);

  // Triggers a debounced backend fetch whenever the user or admin modifies the filters
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchRequestsRef.current();
    }, 500);
    return () => clearTimeout(handler);
  }, [filterDest, filterPickup, filterStatus, filterDate]);



  const filteredData = currentUserId
    ? data.filter(r => Number(r.employee_id) === Number(currentUserId))
    : data;

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setIsViewModalVisible(true);
  };

  const handleCancelRequestInit = (request) => {
    setSelectedRequest(request);
    setIsViewModalVisible(false);
    setIsCancelConfirmModalVisible(true);
  };

  const handleExecuteCancel = async () => {
    if (!selectedRequest) return;
    setCancelLoading(true);
    try {
      await ouberService.updateRequestStatus(selectedRequest.id, { request_status: "Cancelled by User" });
      showSuccessToast("Cancelled", "Trip request successfully cancelled.");
      setIsCancelConfirmModalVisible(false);
      fetchRequests();
    } catch (e) {
      const errorMsg = e.response?.data?.message || "Could not cancel request.";
      showErrorToast("Error", errorMsg);
    } finally {
      setCancelLoading(false);
    }
  };

  const columns = [
    { title: "Request ID", dataIndex: "id", key: "id", align: "center" },
    {
      title: "Request Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status, record) => {
        let tagClass = "tag-pending";
        if (status === "Requested to Join") {
          tagClass = "tag-pending";
        } else if (status === "Approved by AO" || status === "Created by AO") {
          tagClass = "tag-approved";
        } else if (status === "Completed Trip") {
          tagClass = "tag-completed";
        } else if (status === "Declined by AO" || status === "Cancelled by AO" || status === "Cancelled by User") {
          tagClass = "tag-cancelled";
        }

        return (
          <Tag
            className={tagClass}
            style={{
              borderRadius: "12px",
              minWidth: "80px",
              textAlign: "center",
            }}
          >
            {formatStatusWithActor(status, record.status_changed_by)}
          </Tag>
        );
      },
    },
    { title: "Trip Date", dataIndex: "date", key: "date", align: "center" },
    {
      title: "Pickup Venue",
      dataIndex: "pickup",
      key: "pickup",
      align: "center",
    },
    { 
      title: "Destination", 
      dataIndex: "dest", 
      key: "dest", 
      align: "center",
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <EyeOutlined 
            style={{ fontSize: "18px", cursor: "pointer", color: "#1890ff" }} 
            onClick={() => handleViewRequest(record)}
          />
        </Space>
      ),
    },
    { title: "Remarks", dataIndex: "remarks", key: "remarks", align: "center" },
  ];

  const visibleColumns = columns; 

  return (
    <div>
      <Space className="ouber-page-header">
        <Typography.Title level={3} className="ouber-page-title">
          Trip Requests
          <OuberPageInfoIcon
            message="Trip Requests"
            description="View and manage all trip requests you have made. You can cancel at anytime."
          />
        </Typography.Title>
        <Space>
          {onBackToCalendar && (
            <Button
              size="large"
              icon={<CalendarOutlined />}
              onClick={onBackToCalendar}
            >
              Trips Calendar
            </Button>
          )}
          <Button
            size="large"
            type="primary"
            icon={<PlusCircleOutlined />}
            className="ouber-btn-primary"
            style={{ marginBottom: 0 }}
            onClick={() => {
              setSelectedRequest(null);
              setIsRequestModalVisible(true);
            }}
          >
            Request Trip
          </Button>
        </Space>
      </Space>


      <div className="ouber-filter-container">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Pickup Venue" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterPickup}
              onChange={(e) => setFilterPickup(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Destination" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterDest}
              onChange={(e) => setFilterDest(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <DatePicker 
              placeholder="Filter by Date"
              style={{ width: "100%" }}
              format="MM-DD-YYYY"
              value={filterDate}
              onChange={(date) => setFilterDate(date)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Status"
              style={{ width: "100%" }}
              allowClear
              value={filterStatus}
              onChange={(val) => setFilterStatus(val)}
              options={[
                { value: "Completed Trip", label: "Completed Trip" },
                { value: "Approved by AO", label: "Approved by AO" },
                { value: "Requested to Join", label: "Requested to Join" },
                { value: "Declined by AO", label: "Declined by AO" },
                { value: "Cancelled by AO", label: "Cancelled by AO" },
                { value: "Cancelled by User", label: "Cancelled by User" }
              ]}
            />
          </Col>
        </Row>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "12px" }}>
        <DataView />
        <Button
          icon={<SyncOutlined />}
          onClick={fetchRequests}
          className="ouber-btn-primary"
          style={{ marginBottom: 0 }}
        >
          Refresh
        </Button>
      </div>

      {dataView === "table" ? (
        <PvTable
          data={filteredData}
          columns={visibleColumns}
          loading={false}
          onRowClick={() => {}}
        />
      ) : (
        <PvCardList
          data={filteredData}
          columns={visibleColumns}
          onRowClick={() => {}}
          loading={false}
          maxActiveRow={filteredData.length}
        />
      )}

      <UserViewTripReqModal
        isOpen={isViewModalVisible}
        onClose={() => setIsViewModalVisible(false)}
        requestData={selectedRequest}
        onCancelRequest={handleCancelRequestInit}
      />

      <UserCancelConfirmModal
        isOpen={isCancelConfirmModalVisible}
        onClose={() => setIsCancelConfirmModalVisible(false)}
        onConfirm={handleExecuteCancel}
        loading={cancelLoading}
        requestData={selectedRequest}
      />

      <UserRequestTripModal 
        isOpen={isRequestModalVisible}
        onClose={() => setIsRequestModalVisible(false)}
        initialValues={selectedRequest}
        onSave={async (values) => {
          try {
            if (selectedRequest) {
              await ouberService.updateRequestStatus(selectedRequest.id, values);
              showSuccessToast("Success", "Trip request successfully updated.");
            } else {
              await ouberService.createTripRequest(values);
              showSuccessToast("Success", "Trip request successfully created.");
            }
            setIsRequestModalVisible(false);
            fetchRequests();
          } catch (e) {
            const errorMsg = e.response?.data?.message || "Could not save trip request.";
            showErrorToast("Error", errorMsg);
          }
        }}
      />
    </div>
  );
};

export default UserManageTripReqPage;
